// src/mobile_pages/system/User_position.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  listPositions,
  createPosition,
  updatePosition,
  deletePosition,
} from "../../api/users/positions";
import { listCompanies } from "../../api/users/companies";
import api from "../../api/axios";

const EMPTY_POS = { name: "" };

const L = {
  info: (...a) =>
    console.log("%c[POSITION]", "color:#2563eb;font-weight:bold", ...a),
  warn: (...a) => console.warn("[POSITION]", ...a),
  err: (...a) => console.error("[POSITION]", ...a),
};
const parseAxiosError = (e) => {
  try {
    if (e && e.response && e.response.data) {
      return JSON.stringify(e.response.data);
    }
    return e && e.message ? e.message : String(e);
  } catch {
    return String(e);
  }
};

const User_position = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [rows, setRows] = useState([]);
  const [newPos, setNewPos] = useState({ ...EMPTY_POS });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ ...EMPTY_POS });

  const selCompanyNum = selectedCompany ? Number(selectedCompany) : null;

  const loadCompanies = async () => {
    L.info("loadCompanies() 호출");
    try {
      const res = await listCompanies();
      const payload = res?.data ?? res;
      const list = Array.isArray(payload) ? payload : payload?.results ?? [];
      L.info("회사 목록 로드 성공. count:", list.length);
      setCompanies(list);
    } catch (e) {
      L.err("회사 목록 불러오기 실패:", e);
      setCompanies([]);
    }
  };

  const loadPositions = async (companyId) => {
    L.info("loadPositions() 호출, companyId=", companyId);
    if (!companyId) {
      setRows([]);
      return;
    }
    try {
      let url = `positions/?company=${companyId}&page=1`;
      const all = [];
      let safety = 0;
      while (url && safety < 50) {
        const res = await api.get(url);
        const data = res?.data ?? {};
        const chunk = Array.isArray(data) ? data : data.results ?? [];
        all.push(...chunk);
        url = data.next ? data.next.replace(/^.*\/api\//, "") : null;
        safety += 1;
      }
      const filtered = all.filter(
        (r) => Number(r.company?.id ?? r.company) === Number(companyId)
      );
      setRows(filtered);
      L.info("직급 목록 최종 rows:", filtered);
    } catch (e) {
      L.err("직급 목록 불러오기 실패:", e);
      setRows([]);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    L.info("회사 변경 감지 selectedCompany=", selectedCompany);
    setEditId(null);
    loadPositions(selectedCompany);
  }, [selectedCompany]);

  const handleCreate = async () => {
    if (!selCompanyNum) {
      alert("회사를 먼저 선택하세요.");
      return;
    }
    if (!newPos.name.trim()) {
      alert("직급명을 입력하세요.");
      return;
    }
    const payload = { name: newPos.name.trim(), company: selCompanyNum };
    L.info("createPosition() 호출 payload:", payload);
    try {
      await createPosition(payload);
      setNewPos({ ...EMPTY_POS });
      await loadPositions(selCompanyNum);
      alert("직급이 추가되었습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("createPosition() FAIL:", e);
      alert("직급 등록 실패: " + msg);
    }
  };

  const handleUpdate = async (id) => {
    if (!editData.name.trim()) {
      alert("직급명을 입력하세요.");
      return;
    }
    const payload = { name: editData.name.trim(), company: selCompanyNum };
    L.info("updatePosition() 호출, id=", id, "payload:", payload);
    try {
      await updatePosition(id, payload);
      setEditId(null);
      await loadPositions(selCompanyNum);
      alert("수정되었습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("updatePosition() FAIL:", e);
      alert("직급 수정 실패: " + msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    L.info("deletePosition() 호출, id=", id);
    try {
      await deletePosition(id);
      await loadPositions(selCompanyNum);
      alert("삭제되었습니다.");
    } catch (e) {
      const msg = parseAxiosError(e);
      L.err("deletePosition() FAIL:", e);
      alert("직급 삭제 실패: " + msg);
    }
  };

  const startEdit = (row) => {
    if (!row) {
      setEditId(null);
    } else {
      setEditId(row.id);
      setEditData({ name: row.name || "" });
    }
  };

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-4">
        <div className="mobile-page-header">
          <h2 className="mobile-page-title">회사별 직급/역할 관리</h2>
        </div>

        {/* 회사 선택 */}
        <div className="section-card p-3">
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            회사 선택
          </label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="w-full border px-3 py-2 rounded-md bg-white text-sm"
          >
            <option value="">-- 회사 선택 --</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* 등록 */}
        <div className="section-card p-3 space-y-2">
          <p className="text-sm font-semibold">새 직급 등록</p>
          <input
            type="text"
            placeholder="직급명 *"
            value={newPos.name}
            onChange={(e) => setNewPos({ name: e.target.value })}
            className="w-full border rounded-md px-3 py-2 text-sm"
            disabled={!selectedCompany}
          />
          <button
            onClick={handleCreate}
            disabled={!selectedCompany}
            className="btn-create w-full disabled:opacity-50"
          >
            등록
          </button>
        </div>

        {/* 목록 */}
        <div className="space-y-3">
          {rows.map((r) =>
            editId === r.id ? (
              // 수정 모드 카드
              <div key={r.id} className="section-card p-3 space-y-2">
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ name: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdate(r.id)}
                    className="btn-save flex-1"
                  >
                    저장
                  </button>
                  <button
                    onClick={() => startEdit(null)}
                    className="btn-cancel flex-1"
                  >
                    취소
                  </button>
                </div>
              </div>
            ) : (
              // 일반 모드 카드
              <div
                key={r.id}
                className="section-card p-3 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-gray-500">레벨: {r.level}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(r)}
                    className="btn-edit-sm"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    className="btn-delete-sm"
                  >
                    삭제
                  </button>
                </div>
              </div>
            )
          )}
          {rows.length === 0 && (
            <div className="text-center py-10 text-sm text-gray-500">
              {selectedCompany ? "데이터가 없습니다." : "회사를 먼저 선택하세요."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default User_position;
